import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FieldValuesDataSource } from '../../logic/services/field-values-data-source';
import { Permission, PermissionService } from '@vdms-hq/auth';
import { PillColorEnum, ValueFormat } from '@vdms-hq/shared';
import { MatDialog } from '@angular/material/dialog';
import {
  FieldValuesUpsertDialogComponent,
  FieldValuesUpsertDialogInput,
  FieldValuesUpsertDialogResponse,
} from '../field-values-upsert-dialog/field-values-upsert-dialog.component';
import { ApiFieldsSourceService, FieldTypeViewModel, FieldValueViewModel } from '@vdms-hq/selectors';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { ToastService } from '@vdms-hq/toast';
import {
  ActionIdentifier,
  MultipleViewConfiguration,
  ResultsWrapperComponent,
  UIConfirmationDialogService,
  WrapperHeaderAction,
} from '@vdms-hq/ui';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { FieldValuesTreeDialogComponent } from '../field-values-tree-dialog/field-values-tree-dialog.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DynamicFilterInput } from '@vdms-hq/dynamic-filters';
import { FilterType } from '@vdms-hq/fields';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MultipleDataPresentationComponent } from '@vdms-hq/ui';
import { AUDIT_COLUMNS_SORTABLE } from '../../../audit-columns';

@Component({
  selector: 'vdms-hq-selectors-field-values-table',
  templateUrl: './field-values-table.component.html',
  styleUrls: ['./field-values-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MultipleDataPresentationComponent, ReactiveFormsModule, AsyncPipe, TranslatePipe, ResultsWrapperComponent],
})
export class FieldValuesTableComponent implements OnInit, OnDestroy {
  readonly title = input<string>('pages.fields.field_values');
  readonly dialogContext = input<boolean>(false);
  readonly forType = input<FieldTypeViewModel | null>(null);
  readonly selfServiceMode = input<boolean>(false);

  public fieldValuesDataSource = inject(FieldValuesDataSource);
  public readonly matDialog = inject(MatDialog);
  private readonly apiFieldsSourceService = inject(ApiFieldsSourceService);
  public readonly confirmationDialog = inject(UIConfirmationDialogService);
  public readonly toastService = inject(ToastService);
  public readonly permissionService = inject(PermissionService);

  isSuperAdmin = false;

  enabled: string[] = [
    'is_global',
    'uuid',
    'type',
    'label',
    'description',
    'parent_label_with_type',
    'restricted',
    'created_by',
    'created_at',
    'modified_by',
    'modified_at',
    'actions',
  ];

  viewConfiguration: MultipleViewConfiguration<FieldValueViewModel> = {
    tableAdvanced: {
      actions: [],
      columnsEnabled: this.enabled,
      columns: [
        {
          id: 'is_global',
          label: 'pages.fields.columns.is_global',
          valuePath: 'is_global',
          sortable: true,
          viewFormat: {
            modifiers: {
              yesNo: true,
            },
            pills: {
              default: '',
              true: PillColorEnum.OrangeLight,
            },
          },
        },
        {
          id: 'uuid',
          label: 'pages.fields.columns.uuid',
          valuePath: 'uuid',
        },
        {
          id: 'type',
          label: 'pages.fields.columns.type',
          valuePath: 'type.name',
          sortable: true,
          sortObjectPath: 'type.name',
        },
        {
          id: 'label',
          label: 'pages.fields.columns.label',
          valuePath: 'label',
          sortable: true,
        },
        {
          id: 'description',
          label: 'pages.fields.columns.description',
          valuePath: 'description',
          sortable: true,
        },
        {
          id: 'parent_label_with_type',
          label: 'pages.fields.columns.parent_label_with_type',
          valuePath: 'parent_field.label_with_type',
          sortable: true,
          sortObjectPath: 'parent_field.label_with_type',
        },
        {
          id: 'restricted',
          label: 'pages.fields.columns.restricted',
          valuePath: 'extra.restricted',
          sortable: false,
          foldValues: true,
          viewFormat: {
            maxVisibleValues: 2,
          },
        },
        ...AUDIT_COLUMNS_SORTABLE,
        {
          id: 'actions',
          type: 'actions',
        },
      ],
    },
  };

  filters = new FormGroup({
    keyword: new FormControl<string>(''),
  });

  readonly filtersConfig: DynamicFilterInput[] = [
    {
      id: 'keyword',
      label: 'common.fields_editor.table.filter',
      format: ValueFormat.AS_IS,
      filters: {
        objectPath: 'name',
        validFormat: 'keyword',
        type: FilterType.MASTER_TEXT,
      },
      scope: ['other-field-options'],
    },
  ];

  headerActions$: Observable<WrapperHeaderAction[]> = this.permissionService
    .verifyWithOwnedPermissions$([Permission.CREATE_FIELDS])
    .pipe(
      map((hasPermission) =>
        hasPermission
          ? [
              {
                key: 'new',
                name: 'pages.fields.new_field_value',
                color: 'primary',
              },
            ]
          : [],
      ),
    );

  #destroy$ = new Subject<void>();

  #popToast = {
    DELETE_SUCCESS: () =>
      this.toastService.success({
        id: 'delete_select_option_success',
        message: 'notifications.fields.delete.success',
      }),
    DELETE_FAILURE: () =>
      this.toastService.error({
        id: 'delete_select_option_failure',
        message: 'notifications.fields.delete.failure',
      }),
  };

  async ngOnInit() {
    this.fieldValuesDataSource.setTypeFilter(this.forType());
    this.verifyActionsPermissions();
    this.#filterValueChanges();
  }

  ngOnDestroy() {
    this.fieldValuesDataSource.setTypeFilter(null);
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  handleAction($event: { key: ActionIdentifier; item?: FieldValueViewModel }) {
    switch ($event.key) {
      case 'edit':
        if (!$event.item) {
          return;
        }
        this.popEditDialog($event.item);
        break;
      case 'delete':
        if (!$event.item) {
          return;
        }
        this.popDeleteDialog($event.item);
        break;
      case 'new':
        this.popCreateDialog();
        break;
      case 'view-tree':
        if (!$event.item) {
          return;
        }
        this.popTreeDialog($event.item);
        break;
    }
  }

  popCreateDialog = () => {
    const forType = this.forType();
    this.matDialog
      .open<FieldValuesUpsertDialogComponent, FieldValuesUpsertDialogInput, FieldValuesUpsertDialogResponse>(
        FieldValuesUpsertDialogComponent,
        {
          data: {
            createOptions: {
              type: forType,
            },
            selfServiceMode: this.selfServiceMode(),
          },
        },
      )
      .afterClosed()
      .pipe(
        take(1),
        filter((response: FieldValuesUpsertDialogResponse) => !!response),
      )
      .subscribe();
  };

  popEditDialog = (fieldValue: FieldValueViewModel) => {
    this.matDialog
      .open<FieldValuesUpsertDialogComponent, FieldValuesUpsertDialogInput, FieldValuesUpsertDialogResponse>(
        FieldValuesUpsertDialogComponent,
        {
          data: {
            fieldValue,
            selfServiceMode: this.selfServiceMode(),
          },
        },
      )
      .afterClosed()
      .subscribe();
  };

  popDeleteDialog(fieldValue: FieldValueViewModel) {
    this.confirmationDialog
      .openDelete({
        title: 'pages.fields.delete.confirmation_title',
        message: 'pages.fields.delete.confirmation_message',
      })
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() =>
          this.apiFieldsSourceService.deleteFieldValue(fieldValue.uuid).pipe(
            take(1),
            catchError((err) => {
              this.#popToast.DELETE_FAILURE();
              throw err;
            }),
            tap(() => {
              this.#popToast.DELETE_SUCCESS();
            }),
          ),
        ),
      )
      .subscribe();
  }

  verifyActionsPermissions() {
    this.permissionService
      .verifyWithOwnedPermissions$([Permission.EDIT_FIELDS])
      .pipe(
        filter((hasPermission) => hasPermission),
        take(1),
      )
      .subscribe(() => {
        this.viewConfiguration.tableAdvanced?.actions?.push(
          {
            key: 'edit',
            icon: 'edit',
            label: 'Edit',
            e2eId: 'field-value-edit-button',
            onDoubleClick: false,
            disabledTooltip: 'pages.fields.global_readonly_tooltip',
            disabledIf: (item: FieldValueViewModel) => this.selfServiceMode() && !!item.is_global,
          },
          {
            key: 'view-tree',
            icon: 'account_tree',
            label: 'View tree',
          },
        );
      });

    this.permissionService
      .verifyWithOwnedPermissions$([Permission.DELETE_FIELDS])
      .pipe(
        filter((hasPermission) => hasPermission),
        take(1),
      )
      .subscribe(() => {
        this.viewConfiguration.tableAdvanced?.actions?.push({
          key: 'delete',
          icon: 'delete',
          label: 'Delete',
          onDoubleClick: false,
          disabledTooltip: 'pages.fields.global_readonly_tooltip',
          disabledIf: (item: FieldValueViewModel) => this.selfServiceMode() && !!item.is_global,
          fragile: true,
        });
      });
  }

  private popTreeDialog(option: FieldValueViewModel) {
    this.matDialog.open(FieldValuesTreeDialogComponent, {
      data: {
        option,
      },
    });
  }

  #filterValueChanges() {
    this.fieldValuesDataSource.applyFilter('');
    this.filters.valueChanges.pipe(takeUntil(this.#destroy$)).subscribe((value) => {
      this.fieldValuesDataSource.applyFilter(value.keyword ?? '', ['type_name', 'label']);
    });
  }

  get resolvedViewConfiguration(): MultipleViewConfiguration<FieldValueViewModel> {
    return {
      ...this.viewConfiguration,
      multiView: {
        ...(this.viewConfiguration.multiView ?? {}),
        fitToVisibleArea: !this.dialogContext(),
      },
    };
  }
}
